import { useState, useCallback } from 'react'
import { motion } from 'framer-motion'
import { GlassPanel, Button, Icon } from '../components/common'
import { ImportDropzone, ImportPreview, ImportProgress } from '../components/Import'
import { 
  detectFileType, 
  parseCharacterJson, 
  parseFrameJson, 
  parseMarkdown,
  processFramesToShots 
} from '../services/importParser'
import { 
  useCharacterOperations, 
  useShotOperations, 
  useSynopsisOperations 
} from '../hooks/useFirestore'
import { useProject } from '../context/ProjectContext'
import './ImportView.css'

const pageVariants = {
  initial: { opacity: 0, y: 20, filter: 'blur(4px)' },
  animate: { 
    opacity: 1, 
    y: 0, 
    filter: 'blur(0px)',
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  },
  exit: { opacity: 0, y: -20, filter: 'blur(4px)', transition: { duration: 0.2 } }
}

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05
    }
  }
}

const itemVariant = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 }
}

export default function ImportView() {
  const [fileQueue, setFileQueue] = useState([])
  const [previewItem, setPreviewItem] = useState(null)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const [importProgress, setImportProgress] = useState(null)
  const [isImporting, setIsImporting] = useState(false)
  const [filter, setFilter] = useState('all')

  // Project context for scoped data access and permissions
  const { projectId, can } = useProject()
  const canCreate = can('create')

  // Firestore operations (project-scoped)
  const characterOps = useCharacterOperations(projectId)
  const shotOps = useShotOperations(projectId)
  const synopsisOps = useSynopsisOperations(projectId)

  // Handle files selected via dropzone
  const handleFilesSelected = useCallback(async (files) => {
    const newItems = []

    for (const file of files) {
      // Skip non-json/md files
      if (!file.name.endsWith('.json') && !file.name.endsWith('.md')) {
        continue
      }

      try {
        const text = await file.text()
        let content = text
        let parsed = null

        // Parse JSON files
        if (file.name.endsWith('.json')) {
          try {
            content = JSON.parse(text)
          } catch (e) {
            console.error('Failed to parse JSON:', file.name, e)
            continue
          }
        }

        // Detect type and parse
        const type = detectFileType(file.name, content)
        
        switch (type) {
          case 'character':
            parsed = parseCharacterJson(content, file.name)
            break
          case 'shot':
            parsed = parseFrameJson(content, file.name)
            break
          case 'markdown':
            parsed = parseMarkdown(content, file.name)
            break
          default:
            parsed = { raw: content }
        }

        newItems.push({
          id: `${file.name}-${Date.now()}`,
          filename: file.name,
          type,
          content,
          parsed,
          status: 'pending'
        })
      } catch (err) {
        console.error('Failed to read file:', file.name, err)
      }
    }

    setFileQueue(prev => [...prev, ...newItems])
  }, [])

  // Remove item from queue
  const handleRemoveItem = (id) => {
    setFileQueue(prev => prev.filter(item => item.id !== id))
  }

  // Clear all items
  const handleClearAll = () => {
    setFileQueue([])
  }

  // Open preview for an item
  const handlePreview = (item) => {
    setPreviewItem(item)
    setIsPreviewOpen(true)
  }

  // Close preview
  const handleClosePreview = () => {
    setIsPreviewOpen(false)
    setPreviewItem(null)
  }

  // Confirm import from preview (single item)
  const handleConfirmPreview = async (data) => {
    if (!previewItem) return

    try {
      await importSingleItem(previewItem.type, data)
      // Remove from queue
      setFileQueue(prev => prev.filter(item => item.id !== previewItem.id))
      handleClosePreview()
    } catch (err) {
      console.error('Import failed:', err)
      alert(`Import failed: ${err.message}`)
    }
  }

  // Skip item in preview
  const handleSkipPreview = () => {
    if (!previewItem) return
    handleRemoveItem(previewItem.id)
    handleClosePreview()
  }

  // Import a single item
  const importSingleItem = async (type, data) => {
    switch (type) {
      case 'character':
        // Remove internal fields
        const { _importedFrom, _importedAt, ...charData } = data
        await characterOps.add(charData)
        break
      case 'shot':
        // Remove internal fields
        const { _importedFrom: sf, _importedAt: st, _rawData, frameType, ...shotData } = data
        await shotOps.add(shotData)
        break
      case 'markdown':
        await synopsisOps.save({ 
          markdown: data.markdown, 
          title: data.title 
        })
        break
      default:
        throw new Error(`Unknown type: ${type}`)
    }
  }

  // Import all items
  const handleImportAll = async () => {
    if (fileQueue.length === 0) return

    setIsImporting(true)
    setImportProgress({
      total: fileQueue.length,
      completed: 0,
      current: '',
      status: 'importing',
      results: []
    })

    const results = []

    // Group shot frames and process them
    const shotFrames = fileQueue.filter(f => f.type === 'shot')
    const otherItems = fileQueue.filter(f => f.type !== 'shot')

    // Process shots (pair start/end frames)
    if (shotFrames.length > 0) {
      const pairedShots = processFramesToShots(
        shotFrames.map(f => ({ filename: f.filename, content: f.content, parsed: f.parsed }))
      )

      for (const shot of pairedShots) {
        setImportProgress(prev => ({
          ...prev,
          current: shot.shotId || shot.title
        }))

        try {
          await shotOps.add(shot)
          results.push({ filename: shot.shotId, success: true })
        } catch (err) {
          results.push({ filename: shot.shotId, success: false, error: err.message })
        }

        setImportProgress(prev => ({
          ...prev,
          completed: prev.completed + 1,
          results: [...results]
        }))
      }
    }

    // Process other items
    for (const item of otherItems) {
      setImportProgress(prev => ({
        ...prev,
        current: item.filename
      }))

      try {
        await importSingleItem(item.type, item.parsed)
        results.push({ filename: item.filename, success: true })
      } catch (err) {
        results.push({ filename: item.filename, success: false, error: err.message })
      }

      setImportProgress(prev => ({
        ...prev,
        completed: prev.completed + 1,
        results: [...results]
      }))
    }

    setImportProgress(prev => ({
      ...prev,
      status: 'complete',
      results
    }))

    // Clear successfully imported items
    const successFilenames = results.filter(r => r.success).map(r => r.filename)
    setFileQueue(prev => prev.filter(item => !successFilenames.includes(item.filename)))
  }

  // Close progress modal
  const handleCloseProgress = () => {
    setIsImporting(false)
    setImportProgress(null)
  }

  // Get type color for badges
  const getTypeColor = (type) => {
    switch (type) {
      case 'character': return 'var(--accent-cyan)'
      case 'shot': return 'var(--accent-amber)'
      case 'markdown': return 'var(--color-layer-synopsis)'
      default: return 'var(--text-tertiary)'
    }
  }

  // Filter items
  const filteredQueue = filter === 'all' 
    ? fileQueue 
    : fileQueue.filter(item => item.type === filter)

  // Count by type
  const typeCounts = {
    all: fileQueue.length,
    character: fileQueue.filter(f => f.type === 'character').length,
    shot: fileQueue.filter(f => f.type === 'shot').length,
    markdown: fileQueue.filter(f => f.type === 'markdown').length,
  }

  return (
    <motion.div 
      className="import-view"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="import-view__header">
        <h1 className="import-view__title">
          <span className="text-gradient-cyan">Import</span> Data
        </h1>
        <p className="import-view__subtitle">
          Import existing character sheets, frame JSONs, and markdown files
        </p>
      </div>

      {/* Permission warning */}
      {!canCreate && (
        <GlassPanel glow="amber" padding="md" className="import-view__permission-warning">
                <span className="import-view__permission-icon"><Icon name="eye" size={16} /></span>
          <span>You don't have permission to import data to this project. Contact a project admin for access.</span>
        </GlassPanel>
      )}

      {/* Dropzone - only functional if user can create */}
      <ImportDropzone onFilesSelected={canCreate ? handleFilesSelected : () => {}} />

      {/* File Queue */}
      {fileQueue.length > 0 && (
        <GlassPanel padding="lg" className="import-view__queue">
          <div className="queue-header">
            <h3 className="queue-title">
              Import Queue ({fileQueue.length} files)
            </h3>
            <div className="queue-filters">
              {['all', 'character', 'shot', 'markdown'].map(f => (
                <button
                  key={f}
                  className={`queue-filter ${filter === f ? 'queue-filter--active' : ''}`}
                  onClick={() => setFilter(f)}
                >
                  {f.charAt(0).toUpperCase() + f.slice(1)}
                  <span className="queue-filter-count">{typeCounts[f]}</span>
                </button>
              ))}
            </div>
          </div>

          <motion.ul 
            className="queue-list"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            {filteredQueue.map(item => (
              <motion.li 
                key={item.id} 
                className="queue-item"
                variants={itemVariant}
                layout
              >
                <div className="queue-item__info">
                  <span 
                    className="queue-item__type"
                    style={{ backgroundColor: getTypeColor(item.type) }}
                  >
                    {item.type}
                  </span>
                  <span className="queue-item__name">
                    {item.filename}
                  </span>
                  {item.parsed?.name && (
                    <span className="queue-item__parsed">
                      <Icon name="arrow-right" size={14} /> {item.parsed.name}
                    </span>
                  )}
                  {item.parsed?.shotId && (
                    <span className="queue-item__parsed">
                      <Icon name="arrow-right" size={14} /> {item.parsed.shotId}
                    </span>
                  )}
                </div>
                <div className="queue-item__actions">
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handlePreview(item)}
                  >
                    Preview
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    <Icon name="close" size={14} />
                  </Button>
                </div>
              </motion.li>
            ))}
          </motion.ul>

          <div className="queue-actions">
            <Button variant="ghost" onClick={handleClearAll}>
              Clear All
            </Button>
            <Button 
              variant="primary" 
              onClick={handleImportAll}
              disabled={fileQueue.length === 0 || !canCreate}
              title={!canCreate ? 'You don\'t have permission to import' : undefined}
            >
              Import All ({fileQueue.length})
            </Button>
          </div>
        </GlassPanel>
      )}

      {/* Preview Modal */}
      <ImportPreview
        isOpen={isPreviewOpen}
        item={previewItem}
        onClose={handleClosePreview}
        onConfirm={handleConfirmPreview}
        onSkip={handleSkipPreview}
      />

      {/* Progress Modal */}
      <ImportProgress
        isOpen={isImporting}
        progress={importProgress}
        onClose={handleCloseProgress}
      />
    </motion.div>
  )
}

